import { ModalPriority } from '@/app-shell/modal-queue.types';
import { dictateTourBannerDevForceShowAtom } from '@/atoms/dictateTourBannerDevAtoms';
import {
  earnDaysNavPermanentlyDismissedAtom,
  hasSeenPostTrialTooltipAtom,
  hasSeenTrialExtensionModalAtom,
} from '@/atoms/freeTrialModalAtoms';
import { appStateForModalsAtom, launchModalsAtom } from '@/atoms/launchModalsConfigAtom';
import { educationJourneyModalIdAtom } from '@/atoms/modalJourneyObservation';
import {
  PACING_MAX_PER_SESSION,
  isModalPacingEnabledAtom,
  pacedModalSeenCountAtom,
} from '@/atoms/modalPacing';
import {
  currentQueuedModalAtom,
  modalQueueAtom,
  pacedModalsAllowedAtom,
  seenModalIdsAtom,
  userTriggeredModalsAtom,
} from '@/atoms/modalQueueAtoms';
import {
  isModalTierFrameworkEnabledAtom,
  modalTierFrameworkDevOverrideAtom,
} from '@/atoms/modalTierFramework';
import { MODAL_TIER_REGISTER } from '@/atoms/modalTierRegister';
import { GET_USER } from '@/hooks/query/useQueryUser';
import { Tier, evaluateTierGate, resolveTierGateContext } from '@/lib/modalTiers';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { ModalId } from '@heidi/schema/ml-scribe';
import { type UserResponse } from '@heidi/workspace/contracts';
import { produce } from 'immer';
import { type WritableAtom, getDefaultStore } from 'jotai';

// --- Side-state registry ---
// Maps ModalIds to additional atoms and localStorage keys that must be cleared when "unseeing"
type SideState = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  atoms: WritableAtom<boolean, [boolean], any>[];
  localStorageKeys: string[];
};

// To extend: add ModalId.<NEW_MODAL> with { atoms: [hasSeenXAtom], localStorageKeys: ['key'] }.
// unsee() uses this registry to reset related atoms/localStorage beyond seenModalIdsAtom.
const MODAL_SIDE_STATE: Partial<Record<ModalId, SideState>> = {
  // Inline Dictate-page banner — not a queued modal. Reset / unsee must drop
  // the Force-show override or it would keep painting after the tester resets.
  [ModalId.DICTATE_PAGE_TOUR_BANNER]: {
    atoms: [dictateTourBannerDevForceShowAtom],
    localStorageKeys: [],
  },
};

// localStorage keys not tied to a specific ModalId (trial extension, post-trial tooltip)
const GLOBAL_LOCAL_STORAGE_KEYS = [
  'heidi-earn-days-modal-seen',
  'heidi-earn-days-nav-dismissed',
  'heidi:post-trial-tooltip-seen',
  'heidi:doc-export-suggestion-banner-closed-consults',
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const GLOBAL_PERSISTED_ATOMS: WritableAtom<boolean, [boolean], any>[] = [
  hasSeenTrialExtensionModalAtom,
  earnDaysNavPermanentlyDismissedAtom,
  hasSeenPostTrialTooltipAtom,
];

// --- Helpers ---
function safeRemoveItem(key: string) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Storage may be disabled (e.g. private browsing)
  }
}

function safeGetItem(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

const LABEL = '__heidiModals';
const STYLE_LABEL = 'font-weight:bold;color:#6366f1';
const STYLE_RESET = '';

const modalIdValues = new Set(Object.values(ModalId));

/** Type-guard for arbitrary strings (e.g. console input). */
export function isValidModalId(id: string): id is ModalId {
  return modalIdValues.has(id as ModalId);
}

function validateModalId(id: string): ModalId | null {
  if (isValidModalId(id)) return id;

  console.error(
    `%c${LABEL}%c Unknown modal ID: "${id}". Use __heidiModals.all() to see valid IDs.`,
    STYLE_LABEL,
    STYLE_RESET
  );
  return null;
}

/**
 * Surgically delete one record from `data.seen_modals` in the cached
 * UserResponse. Pairs with the atom + side-state resets in
 * {@link unseeModalLocal} so the modal looks unseen to every consumer:
 * launch-queue gating reads `seenModalIdsAtom`, but standalone hooks
 * (`usePinningTooltip`, `useSourceRecommendationBanner`) and modals
 * with custom `MODAL_CLOSED_RULES` read `user.seen_modals?.[id]`
 * directly — without this layer they'd still be suppressed.
 *
 * Optimistic-only: the next user refetch (window-focus refetch is
 * always-on) restores the BE record. Useful for "test what a fresh
 * user sees right now in this tab".
 */
function clearSeenModalCacheRecord(modalId: ModalId): void {
  reactQueryClient.setQueryData<UserResponse>([GET_USER], (prev) => {
    if (!prev?.seen_modals?.[modalId]) return prev;
    return produce(prev, (draft) => {
      if (draft.seen_modals) {
        delete (draft.seen_modals as Record<string, unknown>)[modalId];
      }
    });
  });
}

function clearAllSeenModalCacheRecords(): void {
  reactQueryClient.setQueryData<UserResponse>([GET_USER], (prev) => {
    if (!prev?.seen_modals || Object.keys(prev.seen_modals).length === 0) return prev;
    return produce(prev, (draft) => {
      draft.seen_modals = {};
    });
  });
}

/**
 * Mark a modal as locally unseen across the three layers that gate
 * its visibility:
 *   1. `seenModalIdsAtom`               — queue-managed launch modals
 *   2. side-state atoms / localStorage  — pre-`seen_modals` per-modal flags
 *   3. cached `data.seen_modals` record — what `MODAL_CLOSED_RULES` and
 *                                          direct `user.seen_modals?.[id]`
 *                                          callers read
 *
 * Optimistic only — the next user refetch restores the BE truth.
 * Shared by the `__heidiModals.unsee()` console helper and the
 * dev-tools "Seen modals" UI.
 */
export function unseeModalLocal(modalId: ModalId): void {
  const store = getDefaultStore();

  store.set(seenModalIdsAtom, (prev) => {
    if (!prev) return prev;
    const next = new Set(prev);
    next.delete(modalId);
    return next;
  });

  const sideState = MODAL_SIDE_STATE[modalId];
  if (sideState) {
    for (const atom of sideState.atoms) {
      store.set(atom, false);
    }
    for (const key of sideState.localStorageKeys) {
      safeRemoveItem(key);
    }
  }

  clearSeenModalCacheRecord(modalId);
}

/**
 * Wipe every locally-tracked seen-modal signal. Resets
 * `seenModalIdsAtom` to an **empty** Set (not `null` — `null` blocks
 * all modals while user data loads).
 */
export function unseeAllModalsLocal(): void {
  const store = getDefaultStore();

  store.set(seenModalIdsAtom, new Set<ModalId>());
  // Lift the page-load R5 education mutex too, so a reset can replay a journey.
  store.set(educationJourneyModalIdAtom, null);

  for (const sideState of Object.values(MODAL_SIDE_STATE)) {
    for (const atom of sideState.atoms) {
      store.set(atom, false);
    }
    for (const key of sideState.localStorageKeys) {
      safeRemoveItem(key);
    }
  }

  for (const atom of GLOBAL_PERSISTED_ATOMS) {
    store.set(atom, false);
  }
  for (const key of GLOBAL_LOCAL_STORAGE_KEYS) {
    safeRemoveItem(key);
  }

  clearAllSeenModalCacheRecords();
}

/**
 * Inject a modal into the user-triggered queue at CRITICAL priority
 * after clearing its seen-state so launch-time gating doesn't filter
 * it back out. The component must still be mounted in
 * `GlobalModalsWrapper` and pass any feature-flag gates to render.
 *
 * `DICTATE_PAGE_TOUR_BANNER` is an inline page banner, not a queued
 * modal. Force show sets a session-only atom instead of enqueueing a
 * phantom CRITICAL occupant that would block real modals (including
 * the tour the banner CTA opens).
 *
 * Returns:
 *   - `'shown'`        — successfully injected
 *   - `'already-queued'` — modal is already the visible head, or already
 *     injected as user-triggered (no-op)
 *
 * Do **not** treat "id is in `modalQueueAtom`" as already-showing. That list
 * includes launch occupants that pacing / the tier framework withhold from
 * `currentQueuedModalAtom`. Force-show must still inject a user-triggered
 * copy so those gates are bypassed — otherwise the Seen-modals button
 * reports success (the id is in the queue) while nothing renders.
 */
export type ForceShowResult = 'shown' | 'already-queued';

export function forceShowModalLocal(modalId: ModalId): ForceShowResult {
  const store = getDefaultStore();

  if (modalId === ModalId.DICTATE_PAGE_TOUR_BANNER) {
    unseeModalLocal(modalId);
    store.set(dictateTourBannerDevForceShowAtom, true);
    store.set(userTriggeredModalsAtom, (prev) => prev.filter((m) => m.id !== modalId));
    return 'shown';
  }

  if (store.get(currentQueuedModalAtom)?.id === modalId) {
    return 'already-queued';
  }

  if (store.get(userTriggeredModalsAtom).some((m) => m.id === modalId)) {
    return 'already-queued';
  }

  unseeModalLocal(modalId);

  store.set(userTriggeredModalsAtom, (prev) => {
    if (prev.some((m) => m.id === modalId)) return prev;
    return [
      ...prev,
      {
        id: modalId,
        priority: ModalPriority.CRITICAL,
        dismissible: true,
      },
    ];
  });

  return 'shown';
}

// --- Snapshot/restore for storage-backed atoms ---
// Storage-backed atoms write to localStorage on set, breaking session-scoped behavior.
// Snapshot originals on init and restore on beforeunload.
function snapshotAndRestoreOnUnload(store: ReturnType<typeof getDefaultStore>) {
  const originalValues = GLOBAL_PERSISTED_ATOMS.map((atom) => ({
    atom,
    value: store.get(atom),
  }));
  const originalLocalStorage = GLOBAL_LOCAL_STORAGE_KEYS.map((key) => ({
    key,
    value: safeGetItem(key),
  }));

  window.addEventListener('beforeunload', () => {
    for (const { atom, value } of originalValues) {
      store.set(atom, value);
    }
    for (const { key, value } of originalLocalStorage) {
      if (value === null) {
        safeRemoveItem(key);
      } else {
        try {
          window.localStorage.setItem(key, value);
        } catch {
          // Storage may be disabled
        }
      }
    }
  });
}

// --- Debugger setup ---
function initModalDebugger() {
  if (typeof window === 'undefined') return;
  if (getEnvironment() === ENV_NAME.PROD) return;

  const store = getDefaultStore();
  snapshotAndRestoreOnUnload(store);

  window.__heidiModals = {
    help() {
      console.log(`%c${LABEL}%c Available commands:`, STYLE_LABEL, STYLE_RESET);
      console.table({
        'seen()': 'List all seen modal IDs',
        'queue()': 'Show current modal queue (priority-sorted)',
        'current()': 'Show the currently displayed modal',
        'all()': 'List all valid ModalId values',
        "unsee('MODAL_ID')":
          'Remove a modal from seen set + side-state + cached seen_modals record',
        'unseeAll()': 'Clear all seen modals (atoms + cached records)',
        "forceShow('MODAL_ID')": 'Inject modal into queue at CRITICAL priority',
        'status()': 'Full debug dump (seen, queue, app state, localStorage)',
        'pacing()': 'Show modal-pacing state (flag, cap, quiet period, recording)',
        "pacing('reset')": 'Reset paced session count',
        "pacing('cap')": 'Force paced session count to the cap',
        'tier()': 'Show modal-tier-framework state (flag, register, queue-top verdict)',
        "tier('on' | 'off' | 'follow')":
          'Force the tier framework on/off locally, or follow the PostHog flag',
      });
    },

    seen() {
      const seen = store.get(seenModalIdsAtom);
      if (!seen || seen.size === 0) {
        console.log(`%c${LABEL}%c No modals seen yet.`, STYLE_LABEL, STYLE_RESET);
        return;
      }
      console.log(`%c${LABEL}%c ${seen.size} modal(s) seen:`, STYLE_LABEL, STYLE_RESET);
      console.table([...seen].sort().map((id) => ({ modalId: id })));
    },

    queue() {
      const queue = store.get(modalQueueAtom);
      if (queue.length === 0) {
        console.log(`%c${LABEL}%c Queue is empty.`, STYLE_LABEL, STYLE_RESET);
        return;
      }
      console.log(
        `%c${LABEL}%c ${queue.length} modal(s) in queue:`,
        STYLE_LABEL,
        STYLE_RESET
      );
      console.table(
        queue.map((m) => ({
          modalId: m.id,
          priority: m.priority,
          dismissible: m.dismissible ?? true,
          showOnce: m.showOnce ?? false,
        }))
      );
    },

    current() {
      const current = store.get(currentQueuedModalAtom);
      if (!current) {
        console.log(
          `%c${LABEL}%c No modal currently displayed.`,
          STYLE_LABEL,
          STYLE_RESET
        );
        return;
      }
      console.log(`%c${LABEL}%c Current modal:`, STYLE_LABEL, STYLE_RESET, {
        modalId: current.id,
        priority: current.priority,
        dismissible: current.dismissible ?? true,
      });
    },

    pacing(action?: 'reset' | 'cap') {
      if (action === 'reset') {
        store.set(pacedModalSeenCountAtom, 0);
        console.log(`%c${LABEL}%c Pacing reset.`, STYLE_LABEL, STYLE_RESET);
        return;
      }
      if (action === 'cap') {
        store.set(pacedModalSeenCountAtom, PACING_MAX_PER_SESSION);
        console.log(
          `%c${LABEL}%c Forced cap (${PACING_MAX_PER_SESSION}).`,
          STYLE_LABEL,
          STYLE_RESET
        );
        return;
      }

      const queueTop = store.get(modalQueueAtom).at(0)?.id ?? null;
      const current = store.get(currentQueuedModalAtom)?.id ?? null;
      console.log(`%c${LABEL}%c Pacing state:`, STYLE_LABEL, STYLE_RESET);
      console.table({
        flagEnabled: store.get(isModalPacingEnabledAtom),
        pastQuietPeriod: store.get(appStateForModalsAtom).isUserAtLeastAWeekOld,
        isRecording: store.get(appStateForModalsAtom).isRecording,
        dismissedThisSession: `${store.get(pacedModalSeenCountAtom)} / ${PACING_MAX_PER_SESSION}`,
        pacedAllowed: store.get(pacedModalsAllowedAtom),
        queueTop,
        current,
        withholdingPacedModal: queueTop !== current,
      });
    },

    tier(action?: 'on' | 'off' | 'follow') {
      if (action === 'on' || action === 'off') {
        store.set(modalTierFrameworkDevOverrideAtom, action === 'on');
        console.log(
          `%c${LABEL}%c Tier framework override: forced ${action}.`,
          STYLE_LABEL,
          STYLE_RESET
        );
        return;
      }
      if (action === 'follow') {
        store.set(modalTierFrameworkDevOverrideAtom, null);
        console.log(
          `%c${LABEL}%c Tier framework now follows the PostHog flag.`,
          STYLE_LABEL,
          STYLE_RESET
        );
        return;
      }

      const appState = store.get(appStateForModalsAtom);
      const enabled = store.get(isModalTierFrameworkEnabledAtom);
      const override = store.get(modalTierFrameworkDevOverrideAtom);
      const queueTop = store.get(modalQueueAtom).at(0) ?? null;
      const ctx = resolveTierGateContext({
        isClinicalRecordingActive: appState.isClinicalRecordingActive,
        isUserManaged: appState.isUserManaged,
        isUserAtLeastAWeekOld: appState.isUserAtLeastAWeekOld,
      });

      const topTier = queueTop ? MODAL_TIER_REGISTER[queueTop.id] : undefined;
      let queueTopVerdict: string;
      if (!queueTop) {
        queueTopVerdict = '—';
      } else if (topTier === undefined) {
        queueTopVerdict = 'unmapped → pass-through';
      } else if (!enabled) {
        queueTopVerdict = `T${topTier} ${Tier[topTier]} (framework off)`;
      } else {
        const result = evaluateTierGate(topTier, ctx);
        queueTopVerdict = result.allowed
          ? `T${topTier} ${Tier[topTier]} → allowed`
          : `T${topTier} ${Tier[topTier]} → withheld (${result.reason})`;
      }

      console.log(`%c${LABEL}%c Tier framework state:`, STYLE_LABEL, STYLE_RESET);
      console.table({
        enabled,
        controlSource: override === null ? 'PostHog flag' : 'dev override',
        registeredModals: Object.keys(MODAL_TIER_REGISTER).length,
        recordingActive: appState.isClinicalRecordingActive,
        managedEnterprise: appState.isUserManaged,
        accountAtLeastAWeekOld: appState.isUserAtLeastAWeekOld,
        queueTop: queueTop?.id ?? null,
        queueTopVerdict,
        current: store.get(currentQueuedModalAtom)?.id ?? null,
      });
    },

    all() {
      console.log(
        `%c${LABEL}%c ${modalIdValues.size} ModalId values:`,
        STYLE_LABEL,
        STYLE_RESET
      );
      console.table(
        Object.values(ModalId)
          .sort()
          .map((id) => ({ modalId: id }))
      );
    },

    unsee(id: string) {
      const modalId = validateModalId(id);
      if (!modalId) return;

      unseeModalLocal(modalId);

      console.log(`%c${LABEL}%c Unseen: ${modalId}`, STYLE_LABEL, STYLE_RESET);

      const sideState = MODAL_SIDE_STATE[modalId];
      if (sideState) {
        if (sideState.atoms.length > 0) {
          console.log('  Reset %d side-state atom(s)', sideState.atoms.length);
        }
        if (sideState.localStorageKeys.length > 0) {
          console.log('  Cleared localStorage keys:', sideState.localStorageKeys);
        }
      }

      console.log(
        '  Note: modal may not re-appear if launch conditions are not met.\n' +
          "  Use __heidiModals.forceShow('%s') to bypass conditions.",
        modalId
      );
    },

    unseeAll() {
      unseeAllModalsLocal();

      console.log(
        `%c${LABEL}%c All modals unseen. Side-state atoms, localStorage, and cached seen_modals cleared.`,
        STYLE_LABEL,
        STYLE_RESET
      );
      console.log(
        '  Note: modals will only re-appear if their launch conditions are met.\n' +
          "  Use __heidiModals.forceShow('MODAL_ID') to bypass conditions."
      );
    },

    forceShow(id: string) {
      const modalId = validateModalId(id);
      if (!modalId) return;

      const result = forceShowModalLocal(modalId);
      if (result === 'already-queued') {
        console.warn(
          `%c${LABEL}%c ${modalId} is already queued/displayed.`,
          STYLE_LABEL,
          STYLE_RESET
        );
        return;
      }

      const currentLeader = store.get(currentQueuedModalAtom);
      console.log(
        `%c${LABEL}%c Force-showing: ${modalId} (priority: ${ModalPriority.CRITICAL})`,
        STYLE_LABEL,
        STYLE_RESET
      );
      if (currentLeader && currentLeader.id !== modalId) {
        console.log(
          '  Queued behind: %s (priority: %d)',
          currentLeader.id,
          currentLeader.priority
        );
      }
      console.log(
        '  Note: the modal component must be mounted in GlobalModalsWrapper.\n' +
          '  Some modals are gated behind feature flags and may not render.'
      );
    },

    status() {
      const seen = store.get(seenModalIdsAtom);
      const queue = store.get(modalQueueAtom);
      const current = store.get(currentQueuedModalAtom);
      const appState = store.get(appStateForModalsAtom);
      const launchModals = store.get(launchModalsAtom);

      console.group(`%c${LABEL} status`, STYLE_LABEL);

      // Seen modals
      if (seen && seen.size > 0) {
        console.log('Seen modals (%d):', seen.size, [...seen].sort().join(', '));
      } else {
        console.log('Seen modals: %s', seen === null ? 'loading (null)' : 'none');
      }

      // Current modal
      if (current) {
        console.log('Current modal: %s (priority: %d)', current.id, current.priority);
      } else {
        console.log('Current modal: none');
      }

      // Queue
      if (queue.length > 0) {
        console.log('Queue (%d modals):', queue.length);
        console.table(
          queue.map((m) => ({
            modalId: m.id,
            priority: m.priority,
            dismissible: m.dismissible ?? true,
          }))
        );
      } else {
        console.log('Queue: empty');
      }

      // Launch modals (computed)
      console.log(
        'Launch modals (computed, %d):',
        launchModals.length,
        launchModals.map((m) => m.id).join(', ') || 'none'
      );

      // App state
      console.log('App state:', appState);

      // localStorage
      console.log('Modal-related localStorage:');
      console.table(
        GLOBAL_LOCAL_STORAGE_KEYS.map((key) => ({
          key,
          value: safeGetItem(key),
        }))
      );

      console.groupEnd();
    },
  };
}

initModalDebugger();
